import React, { useMemo, useState } from 'react';
import { Modal, Button, Form, Table, Alert, Spinner } from 'react-bootstrap';
import { parseInvoiceImage, importInvoiceItems } from '../http/invoiceAPI';
import { fetchStores, fetchCategories, fetchProductByBarcode } from '../http/productAPI';

const typeOptions = [
  { value: 'shoe', label: 'Обувь' },
  { value: 'clothes', label: 'Одежда' },
  { value: 'toy', label: 'Игрушки' },
  { value: 'other', label: 'Прочее' }
];

// Базовый URL API без завершающего слэша, чтобы не было "//" в путях картинок
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const InvoiceOcrModal = ({ show, onHide, invoiceId, onImported }) => {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [defaultMarkup, setDefaultMarkup] = useState(20);
  const [productSelectModal, setProductSelectModal] = useState({ show: false, rowIdx: null, products: [] });
  const [searchingBarcode, setSearchingBarcode] = useState({});

  React.useEffect(() => {
    fetchStores().then(setStores).catch(()=>{});
    fetchCategories().then(setCategories).catch(()=>{});
  }, []);

  const handleParse = async () => {
    if (!invoiceId) { setError('Не выбрана накладная'); return; }
    if (!file) { setError('Выберите фото накладной'); return; }
    setError(''); setLoading(true);
    try {
      const res = await parseInvoiceImage(invoiceId, file);
      const parsed = Array.isArray(res.items) ? res.items : [];
      const normalized = parsed.map(item => ({
        barcode: item.barcode || '',
        name: item.name || '',
        quantity: item.quantity || 1,
        purchasePrice: item.purchasePrice || '',
        type: item.type || 'other',
        store_id: item.store_id || '',
        category_id: item.category_id || '',
        description: item.description || '',
        photo: null,
        targetProductId: null,
        matchedProducts: []
      }));
      setRows(normalized);
    } catch (e) {
      setError(e?.response?.data?.message || 'Ошибка распознавания');
    } finally {
      setLoading(false);
    }
  };

  const categoryByType = useMemo(() => {
    return categories.reduce((acc, c) => {
      if (!acc[c.type]) acc[c.type] = [];
      acc[c.type].push(c);
      return acc;
    }, {});
  }, [categories]);

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r,i)=> {
      if (i !== idx) return r;
      const updated = { ...r, [field]: value };
      // При изменении штрих-кода запускаем поиск товаров
      if (field === 'barcode' && value && value.trim().length >= 3) {
        searchProductsByBarcode(idx, value.trim());
      } else if (field === 'barcode' && (!value || value.trim().length < 3)) {
        // Очищаем результаты поиска если штрих-код удалён
        updated.matchedProducts = [];
        updated.targetProductId = null;
      }
      return updated;
    }));
  };

  const searchProductsByBarcode = async (rowIdx, barcode) => {
    if (searchingBarcode[rowIdx]) return; // Уже ищем
    setSearchingBarcode(prev => ({ ...prev, [rowIdx]: true }));
    try {
      const result = await fetchProductByBarcode(barcode);
      if (result.multipleProducts && result.products && result.products.length > 0) {
        // Найдено несколько товаров - просто сохраняем список, окно откроется по клику
        setRows(prev => prev.map((r, i) => 
          i === rowIdx ? { ...r, matchedProducts: result.products } : r
        ));
      } else if (result.id) {
        // Найден один товар - автоматически привязываем
        setRows(prev => prev.map((r, i) => 
          i === rowIdx ? { ...r, targetProductId: result.id, matchedProducts: [result] } : r
        ));
      } else {
        // Товар не найден
        setRows(prev => prev.map((r, i) => 
          i === rowIdx ? { ...r, matchedProducts: [], targetProductId: null } : r
        ));
      }
    } catch (e) {
      // Товар не найден или ошибка - продолжаем без привязки
      setRows(prev => prev.map((r, i) => 
        i === rowIdx ? { ...r, matchedProducts: [], targetProductId: null } : r
      ));
    } finally {
      setSearchingBarcode(prev => {
        const next = { ...prev };
        delete next[rowIdx];
        return next;
      });
    }
  };

  const openProductSelectModal = (rowIdx) => {
    const row = rows[rowIdx];
    if (!row || !row.matchedProducts || row.matchedProducts.length === 0) return;
    setProductSelectModal({ show: true, rowIdx, products: row.matchedProducts });
  };

  const handleSelectProduct = (productId) => {
    const { rowIdx } = productSelectModal;
    setRows(prev => prev.map((r, i) => 
      i === rowIdx ? { ...r, targetProductId: productId } : r
    ));
    setProductSelectModal({ show: false, rowIdx: null, products: [] });
  };

  const handleCreateNewProduct = () => {
    const { rowIdx } = productSelectModal;
    setRows(prev => prev.map((r, i) => 
      i === rowIdx ? { ...r, targetProductId: null } : r
    ));
    setProductSelectModal({ show: false, rowIdx: null, products: [] });
  };
  const updateRowAndBelow = (idx, field, value) => setRows(prev => prev.map((r,i)=> i < idx ? r : { ...r, [field]: value }));
  const updateTypeAndBelow = (idx, value) => setRows(prev => prev.map((r,i)=> i < idx ? r : { ...r, type: value, category_id: '' }));
  const addVariantRow = (idx) => setRows(prev => {
    const base = prev[idx];
    if (!base) return prev;
    const variant = {
      ...base,
      quantity: 1,
      barcode: '',
      photo: null
    };
    return [...prev.slice(0, idx + 1), variant, ...prev.slice(idx + 1)];
  });

  const handleImport = async () => {
    if (!rows.length) { setError('Нет строк для импорта'); return; }
    const filled = rows.filter(r => r.name && Number(r.purchasePrice) > 0 && Number(r.quantity) > 0 && r.store_id && r.category_id);
    if (!filled.length) { setError('Заполните товары: имя, сумма, количество, магазин и категорию'); return; }
    const items = rows.map(r => ({
      barcode: r.barcode,
      name: r.name,
      purchasePrice: Number(r.purchasePrice)||0,
      quantity: Number(r.quantity)||0,
      type: r.type,
      store_id: r.store_id,
      category_id: r.category_id || null,
      description: r.description || '',
      existingProductId: r.targetProductId || null
    }));
    const form = new FormData();
    form.append('invoiceId', invoiceId);
    form.append('defaultMarkup', defaultMarkup);
    form.append('items', JSON.stringify(items));
    rows.forEach((r, idx) => {
      if (r.photo) form.append(`photo_${idx}`, r.photo);
    });
    setLoading(true); setError('');
    try {
      await importInvoiceItems(form);
      if (onImported) onImported();
      onHide();
      setRows([]);
      setFile(null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Ошибка импорта');
    } finally {
      setLoading(false);
    }
  };

  const renderProductSelectModal = () => null;

  return (
    <>
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Распознать накладную (фото/скан)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
          <Form.Control
            type="file"
            accept="image/*,application/pdf"
            onChange={e=>setFile(e.target.files?.[0]||null)}
            onPaste={async e => {
              const item = Array.from(e.clipboardData?.items || []).find(it => it.type.startsWith('image/'));
              if (item) {
                e.preventDefault();
                const blob = item.getAsFile();
                if (blob) {
                  const file = new File([blob], `pasted-${Date.now()}.png`, { type: blob.type });
                  setFile(file);
                }
              }
            }}
            style={{ maxWidth: 360 }}
          />
          <Form.Control
            type="number"
            style={{ maxWidth: 200 }}
            value={defaultMarkup}
            onChange={e=>setDefaultMarkup(Number(e.target.value)||0)}
            placeholder="Наценка %"
          />
          <Button variant="primary" onClick={handleParse} disabled={loading}>Распознать</Button>
        </div>

        {loading && <div className="text-center py-3"><Spinner animation="border" /></div>}

        {rows.length > 0 && (
          <div className="table-responsive" style={{ maxHeight: 420, overflow: 'auto' }}>
            <Table bordered hover size="sm" className="align-middle text-nowrap">
              <thead>
                <tr>
                  <th>Штрих-код</th>
                  <th>Название</th>
                  <th>Кол-во</th>
                  <th>Закупка</th>
                  <th>Тип</th>
                  <th>Категория</th>
                  <th>Описание (необязательно)</th>
                  <th>Фото</th>
                  <th>Магазин</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx} style={{ 
                    backgroundColor: r.targetProductId ? '#fff3cd' : undefined,
                    borderLeft: r.targetProductId ? '4px solid #ffc107' : 'none'
                  }}>
                    <td style={{width:140, position:'relative'}}>
                      <Form.Control 
                        value={r.barcode||''} 
                        onChange={e=>updateRow(idx,'barcode',e.target.value)}
                        onBlur={() => {
                          if (r.barcode && r.barcode.trim().length >= 3 && !r.targetProductId) {
                            searchProductsByBarcode(idx, r.barcode.trim());
                          }
                        }}
                      />
                      {searchingBarcode[idx] && (
                        <div className="position-absolute" style={{right:'4px', top:'50%', transform:'translateY(-50%)', fontSize:'10px'}}>
                          <span className="spinner-border spinner-border-sm" role="status" />
                        </div>
                      )}
                      {r.targetProductId && (
                        <div className="small text-success mt-1" title="Товар будет добавлен в существующую карточку">
                          ✓ Привязан к товару
                        </div>
                      )}
                      {r.matchedProducts && r.matchedProducts.length > 0 && !r.targetProductId && (
                        <div
                          className="mt-1"
                          role="button"
                          style={{ cursor: 'pointer' }}
                          title="Найдены товары с таким штрих-кодом. Нажмите, чтобы выбрать."
                          onClick={() => openProductSelectModal(idx)}
                        >
                          <Button
                            variant="link"
                            size="sm"
                            type="button"
                            className="p-0 small text-warning text-decoration-none"
                          >
                            ⚠ Найдено {r.matchedProducts.length} товар(ов)
                          </Button>
                        </div>
                      )}
                    </td>
                    <td><Form.Control value={r.name||''} onChange={e=>updateRow(idx,'name',e.target.value)} /></td>
                    <td style={{width:90}}><Form.Control type="number" min="0" value={r.quantity||''} onChange={e=>updateRow(idx,'quantity',e.target.value)} /></td>
                    <td style={{width:110}}><Form.Control type="number" min="0" step="0.01" value={r.purchasePrice||''} onChange={e=>updateRow(idx,'purchasePrice',e.target.value)} /></td>
                    <td style={{width:130}}>
                      <Form.Select value={r.type||'other'} onChange={e=>updateTypeAndBelow(idx,e.target.value)}>
                        {typeOptions.map(opt=>(<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                      </Form.Select>
                    </td>
                    <td style={{width:160}}>
                      <Form.Select value={r.category_id||''} onChange={e=>updateRowAndBelow(idx,'category_id',e.target.value)}>
                        <option value="">Без категории</option>
                        {(categoryByType[r.type||'other'] || []).map(c=>(<option key={c.id} value={c.id}>{c.name}</option>))}
                      </Form.Select>
                    </td>
                    <td style={{minWidth:200}}>
                      <Form.Control
                        as="textarea"
                        rows={1}
                        value={r.description||''}
                        placeholder="Описание (опц.)"
                        onChange={e=>updateRow(idx,'description',e.target.value)}
                      />
                    </td>
                    <td style={{minWidth:210}}>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={e=>updateRow(idx,'photo', e.target.files?.[0] || null)}
                        onPaste={async e => {
                          const item = Array.from(e.clipboardData?.items || []).find(it => it.type.startsWith('image/'));
                          if (item) {
                            e.preventDefault();
                            const blob = item.getAsFile();
                            if (blob) {
                              const file = new File([blob], `pasted-${Date.now()}.png`, { type: blob.type });
                              updateRow(idx, 'photo', file);
                            }
                          }
                        }}
                      />
                      {r.photo && <div className="small text-muted mt-1 text-truncate" title={r.photo.name}>{r.photo.name}</div>}
                    </td>
                    <td style={{width:160}}>
                      <Form.Select value={r.store_id||''} onChange={e=>updateRowAndBelow(idx,'store_id',e.target.value)}>
                        <option value="">Магазин</option>
                        {stores.map(s=>(<option key={s.id} value={s.id}>{s.name}</option>))}
                      </Form.Select>
                    </td>
                    <td style={{width:90}}>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-success"
                          size="sm"
                          title="Добавить вариант (копия строки ниже)"
                          onClick={() => addVariantRow(idx)}
                        >
                          +
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>Отмена</Button>
        <Button variant="primary" onClick={handleImport} disabled={loading || rows.length===0}>Импортировать</Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default InvoiceOcrModal;




